
import React, { useEffect } from 'react';
import OrdersHeader from './components/OrdersHeader';
import OrdersListView from './components/OrdersListView';
import OrderAddForm from './components/OrderAddForm';
import OrderEditForm from './components/OrderEditForm';
import { useOrdersData } from './hooks/useOrdersData';
import { useShopFilter } from '@/components/dashboard/shops/hooks/useShopFilter';
import { ShopFilter } from '@/components/dashboard/shops/components/ShopFilter';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { SubscriptionLock } from '@/components/subscription/SubscriptionLock';
import { Skeleton } from '@/components/ui/skeleton';
import LoadingSpinner from '../products/components/LoadingSpinner';

interface OrdersViewProps {
  handlePageChange: (pageId: string) => void;
}

const OrdersView = ({ handlePageChange }: OrdersViewProps) => {
  const { isSubscriptionActive, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
  
  const {
    filteredShops,
    toggleShopSelection,
    isSingleSelectionMode,
    singleSelectedShop,
    selectedShops,
    isLoading: isShopsLoading,
    isCollapsed: isFilterCollapsed,
    setIsCollapsed: setIsFilterCollapsed,
    refreshShops
  } = useShopFilter();

  // Use the shop ID from localStorage if not already selected in the filter
  const currentShopId = singleSelectedShop || localStorage.getItem('selectedShopId') || undefined;

  const {
    orders,
    loading,
    selectedOrders,
    editingOrderId,
    isAdding,
    formatDate,
    formatStatus,
    formatPaymentStatus,
    handleAddOrder,
    handleEditOrder,
    handleOrderUpdated,
    handleOrderAdded,
    handleDeleteOrder,
    handleBulkDeleteOrders,
    handleBackToOrders,
    toggleOrderSelection,
    toggleSelectAll,
    areAllSelected,
    shopId,
    organizationId
  } = useOrdersData({ 
    shopId: currentShopId
  });

  // Si en mode ajout, afficher le formulaire d'ajout
  if (isAdding) {
    return (
      <OrderAddForm 
        handleOrderAdded={handleOrderAdded}
        handleBackToOrders={handleBackToOrders}
        shopId={isSingleSelectionMode ? singleSelectedShop : undefined}
        organizationId={organizationId}
      />
    );
  }

  // Si en mode édition, afficher le formulaire d'édition
  if (editingOrderId) {
    return (
      <OrderEditForm
        orderId={editingOrderId}
        handleOrderUpdated={handleOrderUpdated}
        handleBackToOrders={handleBackToOrders}
        organizationId={organizationId}
      />
    );
  }

  // Rendu du filtre latéral des boutiques, que l'utilisateur ait un abonnement ou non
  const renderShopFilter = () => {
    return (
      <ShopFilter
        isCollapsed={isFilterCollapsed}
        setIsCollapsed={setIsFilterCollapsed}
        shops={filteredShops}
        isLoading={isShopsLoading}
        toggleShopSelection={toggleShopSelection}
        isSingleSelectionMode={isSingleSelectionMode}
        singleSelectedShop={singleSelectedShop}
        selectedShops={selectedShops}
        showFilterButton={true}
        title="Boutiques"
        filterButtonLabel="Filtrer les commandes"
        refreshShops={refreshShops}
      />
    );
  };

  // Afficher l'état de chargement pendant la vérification de l'abonnement
  if (isSubscriptionLoading || isSubscriptionActive === null) {
    return (
      <div className="flex h-full">
        {renderShopFilter()}
        <div className="flex-1 bg-pastel-blue-light dark:bg-[#121624] flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Vérifier si l'abonnement est actif pour afficher la vue des commandes
  if (isSubscriptionActive === false) {
    return (
      <div className="flex h-full">
        {renderShopFilter()}
        <div className="flex-1 bg-pastel-blue-light dark:bg-[#121624]">
          <SubscriptionLock 
            title="Commandes verrouillées"
            description="La gestion des commandes n'est disponible qu'avec un abonnement actif."
            buttonText="Débloquer la gestion des commandes"
          />
        </div>
      </div>
    );
  }

  // Sinon, afficher la liste des commandes
  return (
    <div className="flex h-full">
      {renderShopFilter()}

      <div className="flex-1 bg-pastel-blue-light dark:bg-[#121624]">
        <div className="container mx-auto px-4 py-6">
          <OrdersHeader 
            isAdding={false} 
            shopId={currentShopId}
            organizationId={organizationId}
            selectedOrders={selectedOrders}
          />
          
          <OrdersListView
            orders={orders}
            loading={loading}
            selectedOrders={selectedOrders}
            toggleOrderSelection={toggleOrderSelection}
            handleEditOrder={handleEditOrder}
            handleDeleteOrder={handleDeleteOrder}
            handleBulkDeleteOrders={handleBulkDeleteOrders}
            formatDate={formatDate}
            formatStatus={formatStatus}
            formatPaymentStatus={formatPaymentStatus}
            toggleSelectAll={toggleSelectAll}
            areAllSelected={areAllSelected}
            handleAddOrder={handleAddOrder}
            organizationId={organizationId}
          />
        </div>
      </div>
    </div>
  );
};

export default OrdersView;
